import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Paper, Button } from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllDocumentCategorys } from 'src/shared/services/documentCategoryService';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import PageTabNavigation, { PageTabItem } from 'src/shared/components/navigations/pageTabNavigation';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { DocumentArchiveDataGrid } from '../components/documentArchiveDataGrid';
import { DocumentSearchFilter } from '../models/documentSearchFilter';
import { DocumentCategorySearchFilter } from '../models/documentCategorySearchFilter';
import { DocumentCategory } from '../models/documentCategory';
import { UploadDocumentFile } from '../components/uploadDocumentFile';
import { DocumentDetailsModal } from '../components/documentDetailsModal';

export const DocumentArchiveOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(false);
  const [openUploadFile, setOpenUploadFile] = useState<boolean>(false);
  const [documentCategories, setDocumentCategories] = useState<DocumentCategory[]>();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [openDocumentIdDetails, setDocumentIdDetails] = useState<number | null>(null);
  const [refetchDataGrid, setRefetchDataGrid] = useState<boolean>(false);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [documentSearchFilter, setDocumentSearchFilter] = useState<DocumentSearchFilter>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    sortCol: 'created',
    sortDir: 'asc',
    filter: '',
    documentCategoryId: undefined,
  });

  const pageTabs: PageTabItem[] = [
    {
      label: t('documentArchive:files'),
      route: RoutesEnum.DocumentArchiveOverview,
    },
    {
      label: t('documentArchive:categories'),
      route: RoutesEnum.DocumentCategoryOverview,
    },
  ];

  const categoryFilter = useMemo<DocumentCategorySearchFilter>(
    () => ({
      pageIndex: 0,
      pageSize: 1000,
      sortCol: 'category',
      sortDir: 'asc',
      filter: '',
    }),
    [],
  );

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllDocumentCategorys(categoryFilter);
      if (isMounted()) {
        const allCategories = result.data.results;

        // Skapar en kategori (Övrigt)
        const otherCategory = {
          documentCategory_id: 0,
          category: t('documentArchive:documentCategory.other'),
          description: '',
        };

        const mergedCategories = [otherCategory, ...allCategories];
        setDocumentCategories(mergedCategories);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [categoryFilter, isMounted, t]);

  useEffect(() => {
    if (isMounted()) {
      fetchCategories();
    }
  }, [fetchCategories, isMounted]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setDocumentSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setDocumentSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setDocumentSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, tempSearchString]);

  return (
    <>
      <GridWrapper>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <MainContentHeader title={t('documentArchive:documentArchive')} />
        </Grid>

        <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setOpenUploadFile(true)}
            sx={{ marginLeft: 2 }}
            variant='contained'
            color='primary'
            startIcon={<FileUpload fontSize='small' />}
            disableElevation
          >
            {t('documentArchive:uploadFile')}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <PageTabNavigation pageTabs={pageTabs} />
            <PageFilterSection
              leftContent={
                <SearchField
                  initialValue={documentSearchFilter.filter || ''}
                  onSearch={(newSearchString) => {
                    setSearchInitialized(true);
                    setTempSearchString(newSearchString);
                  }}
                />
              }
              rightContent={
                documentCategories &&
                documentCategories.length > 0 && (
                  <Box sx={{ display: 'flex' }}>
                    <AutocompleteDropdown<DocumentCategory>
                      keyProp='documentCategory_id'
                      nameProp='category'
                      label={t('invoice:articleRegister.category')}
                      emptyLabel={documentCategories?.length === 0 ? t('documentArchive:documentCategory.noCategories') : t('common:button.choose')}
                      placeholder={t('common:button.choose')}
                      options={documentCategories || []}
                      callback={(selectedOption) => {
                        if (selectedOption) {
                          setDocumentSearchFilter((prev) => ({
                            ...prev,
                            pageIndex: 0,
                            documentCategoryId: selectedOption.documentCategory_id ?? undefined,
                          }));
                        } else {
                          setDocumentSearchFilter((prev) => ({
                            ...prev,
                            pageIndex: 0,
                            documentCategoryId: undefined,
                          }));
                        }
                      }}
                    />
                  </Box>
                )
              }
            />
            <Box className='section-inset'>
              <DocumentArchiveDataGrid
                searchFilter={documentSearchFilter}
                searchFilterCallback={(newFilter) =>
                  setDocumentSearchFilter((prev) => ({
                    ...prev,
                    ...newFilter,
                  }))
                }
                refetchDataGrid={refetchDataGrid}
                setRefetchDataGrid={setRefetchDataGrid}
                setDocumentIdDetails={(val: number) => {
                  if (val && val > 0) {
                    setDocumentIdDetails(val);
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </GridWrapper>

      <UploadDocumentFile
        openModal={openUploadFile}
        setOpenModal={setOpenUploadFile}
        documentCategories={documentCategories || []}
        refetchDataGrid={(val: boolean) => {
          if (val) {
            setRefetchDataGrid(true);
          }
        }}
      />
      {openDocumentIdDetails && (
        <DocumentDetailsModal
          documentId={openDocumentIdDetails}
          clearDokumentId={setDocumentIdDetails}
          categoryFilter={categoryFilter}
          refetchDataGrid={(val: boolean) => {
            if (val) {
              setRefetchDataGrid(true);
            }
          }}
        />
      )}
    </>
  );
};
